/**
 * Layout → Overlay / Grid: every ship on one chart, or one panel per ship.
 *
 * One control for the one decision, used by Explore and the SFOC viewer alike,
 * so the label, the titles, the look and the keyboard are the same on both. The
 * parent owns the value (`value` + the `select` callback, like `plain-select`)
 * and decides when the choice is offered at all - this only draws it.
 *
 * It is a radio group, and behaves like one: Tab reaches the chosen segment
 * only, the arrow keys move the choice, and a disabled Overlay is skipped. A
 * disabled Overlay keeps its reason as the title, so why it cannot be chosen is
 * visible where the choice would be.
 */

import { html, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';

export type ChartLayout = 'overlay' | 'grid';

const LAYOUTS: ReadonlyArray<{ id: ChartLayout; label: string; title: string }> = [
  { id: 'overlay', label: 'Overlay', title: 'Every ship on one chart' },
  { id: 'grid', label: 'Grid', title: 'One panel per ship, same colours and scales' },
];

/**
 * Where an arrow key moves the choice, or null when it does not move it.
 * Two options, so any arrow toggles - unless the other one is disabled.
 */
export function layoutAfterKey(
  current: ChartLayout,
  key: string,
  overlayDisabled: boolean,
): ChartLayout | null {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
    return null;
  }
  let next: ChartLayout;
  if (key === 'Home') next = 'overlay';
  else if (key === 'End') next = 'grid';
  else next = current === 'overlay' ? 'grid' : 'overlay';
  if (next === 'overlay' && overlayDisabled) return null;
  return next === current ? null : next;
}

@customElement('layout-select')
export class LayoutSelect extends LightElement {
  @property() value: ChartLayout = 'overlay';
  /** Why Overlay cannot be used here; setting it disables Overlay and shows Grid. */
  @property({ attribute: false }) overlayUnavailable: string | null = null;
  /** Called with the chosen layout when the person picks the other one. */
  @property({ attribute: false }) select: (value: ChartLayout) => void = () => {};

  private get shown(): ChartLayout {
    return this.overlayUnavailable ? 'grid' : this.value;
  }

  private choose(next: ChartLayout, focus = false): void {
    if (next === this.shown) return;
    this.select(next);
    if (focus) {
      void this.updateComplete.then(() =>
        this.querySelector<HTMLElement>(`[data-layout="${next}"]`)?.focus(),
      );
    }
  }

  private onKey = (e: KeyboardEvent): void => {
    const next = layoutAfterKey(this.shown, e.key, this.overlayUnavailable != null);
    if (!next) return;
    e.preventDefault();
    this.choose(next, true);
  };

  override render(): TemplateResult {
    const shown = this.shown;
    return html`
      <div class="axis-block layout-select">
        <label>Layout</label>
        <div class="seg" role="radiogroup" aria-label="Chart layout" @keydown=${this.onKey}>
          ${LAYOUTS.map(({ id, label, title }) => {
            const disabled = id === 'overlay' && this.overlayUnavailable != null;
            return html`<button
              type="button"
              class="seg-btn ${shown === id ? 'active' : ''}"
              role="radio"
              data-layout=${id}
              aria-checked=${shown === id ? 'true' : 'false'}
              tabindex=${shown === id ? 0 : -1}
              ?disabled=${disabled}
              title=${disabled ? this.overlayUnavailable! : title}
              @click=${() => this.choose(id)}
            >
              ${label}
            </button>`;
          })}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'layout-select': LayoutSelect;
  }
}
